import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { extractTemplateSources, interpolateTextAsync } from "../../shared/template";
import type { TemplateContext } from "../../shared/template";
import { useEnduserStore } from "../store/hooks";
import { useOrganization } from "../../shared/store/organizationStore";
import { useSeasonBanner } from "../../shared/store/portalSchemaStore";
import { peekPendingPartnerRedirect } from "../utils/pendingPartnerRedirect";
import { appendRedirectQueryParam } from "../utils/appendRedirectQueryParam";

/**
 * Resolves a raw href containing async placeholders (e.g. {{season.token}},
 * {{welle.token}}) at click time and opens the result in a new tab. Shared by
 * AsyncLinkInterceptor (delegated clicks inside author HTML) and ButtonBlock
 * (a single configured href), so both partners' links behave identically.
 *
 * Pass the raw href — the one still holding placeholders, not a sync-interpolated
 * copy — since the partner is identified from its placeholders.
 *
 * The token is minted on click, not prefetched into the href, because it is a
 * single-use per-click JWT (see `redirectTemplate` in shared/schema/portal-schema.ts).
 * Prefetching would mint one per pageview whether or not the link is used, leave a
 * copyable bearer credential sitting in the DOM, and be expired by the time a
 * long-lived portal tab is actually clicked. It also could not cover the
 * AsyncLinkInterceptor path at all, where hrefs live inside author-authored HTML
 * that no component owns.
 */
export const useAsyncHrefNavigation = () => {
  const { t } = useTranslation("common");
  const session = useEnduserStore((state) => state.session);
  const enduser = useEnduserStore((state) => state.currentEnduser);
  const organization = useOrganization();
  const seasonBanner = useSeasonBanner();
  /**
   * Tokens are single-use, so a double-click must not mint twice — the second mint can
   * invalidate the token in the tab the patient just opened. A ref, not state, so the
   * interceptor's author-HTML subtree doesn't re-render on every click.
   */
  const inFlightRef = useRef(new Set<string>());

  return useCallback(
    async (rawHref: string) => {
      if (!session) {
        toast.error(t("Unable to open link. Please try again."));
        return;
      }
      if (inFlightRef.current.has(rawHref)) return;

      const templateContext: TemplateContext = {
        enduser,
        organization,
        snippets: {},
      };

      inFlightRef.current.add(rawHref);
      try {
        const resolved = await interpolateTextAsync(
          rawHref,
          templateContext,
          { session, enduser, organization },
          { banner: seasonBanner ?? "" }
        );
        // dietitian_portal_redirect_to is Season's redirect-back contract; other
        // partners' URLs must not carry it.
        const finalUrl = extractTemplateSources(rawHref).has("season")
          ? appendRedirectQueryParam(resolved, peekPendingPartnerRedirect())
          : resolved;
        window.open(finalUrl, "_blank", "noopener,noreferrer");
      } catch {
        toast.error(t("Unable to open link. Please try again."));
      } finally {
        inFlightRef.current.delete(rawHref);
      }
    },
    [session, enduser, organization, seasonBanner, t]
  );
};
